import React, { useState, useRef, useEffect } from 'react';
import { Download, Trash2, ArrowLeft, Move, Undo, Database, Image } from 'lucide-react';
import onhireService from '../../services/OnHireService.js';
import axiosInstance from '../../config/axios.js';
import tankType1 from '../../static/images/tank_onhire/tank_type_1.jpeg';
import tankType2 from '../../static/images/tank_onhire/tank_type_2.jpeg';

// Complete legend definitions for both image types
const LEGENDS = {
  type2: {
    title: 'Tank Views - Front & Rear',
    categories: [
      {
        name: 'LINED TANK',
        items: [
          { id: 'LT', label: 'LINED Tank', symbol: '□', color: '#FFFFFF' }
        ]
      },
      {
        name: 'Lining Material',
        items: [
          { id: 'LM', label: 'Lining Material:', symbol: '', color: '#F5F5F5' }
        ]
      },
      {
        name: 'Surface Condition',
        items: [
          { id: 'D', label: 'Small dent', symbol: 'D', color: '#FFE6E6' },
          { id: 'LR', label: 'Lining repair', symbol: 'LR', color: '#E6F3FF' },
          { id: 'SC+', label: 'Superficial Scratches', symbol: 'SC+', color: '#FFF4E6' }
        ]
      },
      {
        name: 'SS TANK',
        items: [
          { id: 'X', label: 'SS Tank', symbol: 'X', color: '#F0F0F0' }
        ]
      },
      {
        name: 'Ox Baffles fitted: SS Bolted / Tack Welded',
        items: [
          { id: 'OX', label: 'Ox Baffles fitted: SS Bolted / Tack Welded', symbol: '', color: '#F5F5F5' }
        ]
      },
      {
        name: 'Surface Defects',
        items: [
          { id: 'PT', label: 'Pitted', symbol: 'PT', color: '#FFB6C1' },
          { id: 'DC', label: 'Discoloured', symbol: 'DC', color: '#DDA0DD' },
          { id: 'P', label: 'Polished', symbol: 'P', color: '#98FB98' },
          { id: 'G', label: 'Ground', symbol: 'G', color: '#D3D3D3' },
          { id: 'DN', label: 'Dented', symbol: 'D', color: '#FFE4B5' },
          { id: 'SC', label: 'Scratched', symbol: 'SC', color: '#F0E68C' },
          { id: 'CO', label: 'Minor corrosion affected', symbol: 'CO', color: '#CD853F' },
          { id: 'TM', label: 'Toolmarks', symbol: 'TM', color: '#B0C4DE' },
          { id: 'ER', label: 'Existing rep', symbol: 'ER', color: '#ADD8E6' }
        ]
      }
    ]
  },
  type1: {
    title: 'Tank Sections - Front & Rear Grid',
    categories: [
      {
        name: 'Dent Classification (Depth)',
        items: [
          { id: 'D1', label: 'D1: Dented 5-10mm', symbol: 'D1', color: '#FFE6E6' },
          { id: 'D2', label: 'D2: Dented 10-15mm', symbol: 'D2', color: '#FFCCCC' },
          { id: 'D3', label: 'D3: Dented 15-20mm', symbol: 'D3', color: '#FFB3B3' },
          { id: 'D4', label: 'D4: Dented 20-25mm', symbol: 'D4', color: '#FF9999' },
          { id: 'D5', label: 'D5: Dented 25-40mm', symbol: 'D5', color: '#FF8080' },
          { id: 'D6', label: 'D6: Dented >40mm', symbol: 'D6', color: '#FF6666' }
        ]
      },
      {
        name: 'Product & Structural Issues',
        items: [
          { id: 'AF', label: 'AF: Product Affected', symbol: 'AF', color: '#FFE4B5' },
          { id: 'BR', label: 'BR: Broken', symbol: 'BR', color: '#FFA07A' },
          { id: 'BT', label: 'BT: Bent', symbol: 'BT', color: '#FFB366' },
          { id: 'BU', label: 'BU: Burned', symbol: 'BU', color: '#FF8C69' },
          { id: 'CR', label: 'CR: Cracked', symbol: 'CR', color: '#CD853F' },
          { id: 'CT', label: 'CT: Cut', symbol: 'CT', color: '#DEB887' },
          { id: 'CM', label: 'CM: Customer Marks', symbol: 'CM', color: '#F4A460' },
          { id: 'CO', label: 'CO: Corrosion', symbol: 'CO', color: '#D2691E' }
        ]
      },
      {
        name: 'Existing Modifications',
        items: [
          { id: 'EW', label: 'EW: Existing Weld', symbol: 'EW', color: '#B0C4DE' },
          { id: 'EI', label: 'EI: Existing Insert', symbol: 'EI', color: '#ADD8E6' },
          { id: 'EP', label: 'EP: Existing Patch', symbol: 'EP', color: '#87CEEB' },
          { id: 'ER', label: 'ER: Existing Repair', symbol: 'ER', color: '#87CEFA' },
          { id: 'ES', label: 'ES: Existing Section', symbol: 'ES', color: '#6495ED' }
        ]
      },
      {
        name: 'Surface Conditions',
        items: [
          { id: 'GL', label: 'GL: Glue Marks', symbol: 'GL', color: '#F0E68C' },
          { id: 'GO', label: 'GO: Gouged Material', symbol: 'GO', color: '#BDB76B' },
          { id: 'GR', label: 'GR: Ground', symbol: 'GR', color: '#D3D3D3' },
          { id: 'HM', label: 'HM: Hammer Marks', symbol: 'HM', color: '#C0C0C0' },
          { id: 'HO', label: 'HO: Holed', symbol: 'HO', color: '#A9A9A9' },
          { id: 'IR', label: 'IR: Improper Repair', symbol: 'IR', color: '#FFB6C1' },
          { id: 'LO', label: 'LO: Loose', symbol: 'LO', color: '#FFDAB9' },
          { id: 'MS', label: 'MS: Missing', symbol: 'MS', color: '#FFE4E1' }
        ]
      },
      {
        name: 'Material Finish',
        items: [
          { id: 'PE', label: 'PE: Peeling', symbol: 'PE', color: '#FFC0CB' },
          { id: 'PL', label: 'PL: Over Plated', symbol: 'PL', color: '#DDA0DD' },
          { id: 'RS', label: 'RS: Re-Secured', symbol: 'RS', color: '#E6E6FA' },
          { id: 'SC', label: 'SC: Scratched', symbol: 'SC', color: '#F0F0F0' },
          { id: 'SL', label: 'SL: Sealed', symbol: 'SL', color: '#98FB98' },
          { id: 'ST', label: 'ST: Stained', symbol: 'ST', color: '#FFFACD' }
        ]
      }
    ]
  }
};

// Image source mode: 'db' = loaded from database, 'static' = local static file
const IMAGE_SOURCE_MODE = {
  DB: 'db',
  STATIC: 'static',
};

const TankCanvasEditor = ({
  imageType = 'type1',
  onSave,
  onBack,
  initialData = null,
  existingImage = null,
  // Optional: pass the onhire ID to attempt loading the image from the database
  onhireId = null,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // ── Determine initial marks ───────────────────────────────────────────────
  // Priority: existingImage.marks (editing saved canvas) → initialData.marks → []
  const getInitialMarks = () => {
    if (existingImage?.marks?.length > 0) return existingImage.marks;
    if (initialData?.marks?.length > 0) return initialData.marks;
    return [];
  };

  const [marks, setMarks] = useState(getInitialMarks);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [selectedMarkIndex, setSelectedMarkIndex] = useState(null);
  const [isDraggingMark, setIsDraggingMark] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // editMode: 'edit' = keep existing marks, 'new' = start fresh
  const [editMode, setEditMode] = useState(existingImage?.marks?.length > 0 ? 'edit' : 'new');

  // Image source state: which source is currently active and availability
  const [imageSourceMode, setImageSourceMode] = useState(IMAGE_SOURCE_MODE.STATIC);
  const [dbImageAvailable, setDbImageAvailable] = useState(false);
  const [dbImageLoading, setDbImageLoading] = useState(false);
  const [dbImageUrl, setDbImageUrl] = useState(null);

  const legend = LEGENDS[imageType] || LEGENDS.type1;

  // Static image sources — always the clean template (not flattened PNG)
  // so marks remain live/interactive on top of the background
  const staticImageSources = {
    type1: tankType1,
    type2: tankType2,
  };

  // ── When existingImage changes externally, reload marks into state ────────
  useEffect(() => {
    if (existingImage?.marks) {
      setMarks(existingImage.marks);
      setEditMode('edit');
    }
  }, [existingImage]);

  // ── Resolve DB image URL from onhireService ──────────────────────────────
  const resolveDbImageUrl = () => {
    if (!onhireId) return null;
    if (imageType === 'type1') return `/onhires/${onhireId}/image/1`;
    if (imageType === 'type2') return `/onhires/${onhireId}/image/2`;
    return null;
  };

  const fetchDbImageAsBase64 = async (relativeUrl) => {
    const response = await axiosInstance.get(relativeUrl, { responseType: 'blob' });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(response.data);
    });
  };

  // ── On mount: fetch DB image as base64 to avoid CORS canvas-taint issues ──
  // Using fetch() instead of new Image() probe because:
  // 1. fetch() sends session cookies (same-origin), so auth works
  // 2. Converting to base64 data URL lets us drawImage() on canvas without
  //    needing Access-Control-Allow-Origin headers from the server
  useEffect(() => {
    let cancelled = false;

    const loadDbImage = async () => {
      // If existingImage was already confirmed to exist in EditOnHire (fromDb: true),
      // we can use its URL directly — no need to re-probe
      const urlFromParent =
        existingImage?.fromDb && existingImage?.imageData && !existingImage.imageData.startsWith('data:')
          ? existingImage.imageData
          : null;

      const url = urlFromParent || resolveDbImageUrl();

      if (!onhireId && !urlFromParent) {
        setDbImageAvailable(false);
        setImageSourceMode(IMAGE_SOURCE_MODE.STATIC);
        return;
      }

      if (!url) {
        setDbImageAvailable(false);
        setImageSourceMode(IMAGE_SOURCE_MODE.STATIC);
        return;
      }

      setDbImageLoading(true);

      try {
        // axiosInstance automatically attaches Bearer token from localStorage
        const response = await axiosInstance.get(url, { responseType: 'blob' });
        const reader = new FileReader();

        reader.onload = () => {
          if (cancelled) return;
          setDbImageUrl(reader.result); // base64 data URL — safe for canvas
          setDbImageAvailable(true);
          setImageSourceMode(IMAGE_SOURCE_MODE.DB);
          setDbImageLoading(false);
        };

        reader.onerror = () => {
          if (cancelled) return;
          setDbImageAvailable(false);
          setImageSourceMode(IMAGE_SOURCE_MODE.STATIC);
          setDbImageLoading(false);
        };

        reader.readAsDataURL(response.data);
      } catch (err) {
        if (cancelled) return;
        setDbImageAvailable(false);
        setImageSourceMode(IMAGE_SOURCE_MODE.STATIC);
        setDbImageLoading(false);
      }
    };

    loadDbImage();

    return () => { cancelled = true; };
  }, [onhireId, imageType, existingImage?.imageData]);

  // ── Load background image whenever source mode or dbImageUrl changes ──────
  // dbImageUrl is now always a base64 data URL (no CORS issues on canvas)
  useEffect(() => {
    const img = new window.Image();
    // No crossOrigin needed for base64 data URLs or same-origin static files

    if (imageSourceMode === IMAGE_SOURCE_MODE.DB && dbImageUrl) {
      img.src = dbImageUrl; // base64 — safe to draw on canvas
    } else {
      img.src = staticImageSources[imageType];
    }

    img.onload = () => {
      setBackgroundImage(img);
    };
    img.onerror = () => {
      if (imageSourceMode === IMAGE_SOURCE_MODE.DB) {
        // Fallback to static if DB image somehow fails
        const fallback = new window.Image();
        fallback.src = staticImageSources[imageType];
        fallback.onload = () => setBackgroundImage(fallback);
        fallback.onerror = () => console.error('Failed to load fallback static image');
      } else {
        console.error('Failed to load background image');
      }
    };
  }, [imageType, imageSourceMode, dbImageUrl]);

  // Draw canvas whenever marks or background change
  useEffect(() => {
    drawCanvas();
  }, [marks, backgroundImage, selectedMarkIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if (e.key === 'Delete' && selectedMarkIndex !== null) {
        e.preventDefault();
        handleDeleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [marks, selectedMarkIndex]);

  const findLegendItem = (symbolId) => {
    for (const category of legend.categories) {
      const item = category.items.find(i => i.id === symbolId);
      if (item) return item;
    }
    return null;
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);

    // Draw background image
    if (backgroundImage) {
      try {
        ctx.globalAlpha = 0.85;
        ctx.drawImage(backgroundImage, 0, 0, width, height);
        ctx.globalAlpha = 1;
      } catch (e) {
        console.error('Error drawing background image', e);
      }
    }

    // Draw border
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, width, height);

    // Draw marks
    marks.forEach((mark, index) => {
      const legendItem = findLegendItem(mark.symbolId);
      if (legendItem) {
        const isSelected = index === selectedMarkIndex;
        const boxSize = 40;
        const halfBox = boxSize / 2;

        // Draw selection highlight
        if (isSelected) {
          ctx.strokeStyle = '#3B82F6';
          ctx.lineWidth = 3;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(mark.x - halfBox - 2, mark.y - halfBox - 2, boxSize + 4, boxSize + 4);
          ctx.setLineDash([]);
        }

        // Draw symbol background with semi-transparency
        ctx.fillStyle = legendItem.color;
        ctx.globalAlpha = 0.3;
        ctx.fillRect(mark.x - halfBox, mark.y - halfBox, boxSize, boxSize);
        ctx.globalAlpha = 1;

        // Draw border
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.strokeRect(mark.x - halfBox, mark.y - halfBox, boxSize, boxSize);

        // Draw text/symbol
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(legendItem.symbol || legendItem.id, mark.x, mark.y);
      }
    });
  };

  const drawLegendOverlay = (ctx, canvasWidth, canvasHeight) => {
    const padding = 10;
    const boxWidth = 35;
    const boxHeight = 30;
    const fontSize = 10;
    const spacing = 5;
    const itemsPerRow = imageType === 'type1' ? 6 : 8;
    
    const allItems = legend.categories.flatMap(cat => cat.items);
    
    const rows = Math.ceil(allItems.length / itemsPerRow);
    const legendHeight = rows * (boxHeight + spacing) + (padding * 2);
    const legendWidth = Math.min(canvasWidth - (padding * 2), itemsPerRow * (boxWidth + spacing) + padding);
    
    const legendX = (canvasWidth - legendWidth) / 2;
    const legendY = canvasHeight - legendHeight - padding;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillRect(legendX, legendY, legendWidth, legendHeight);
    
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 2;
    ctx.strokeRect(legendX, legendY, legendWidth, legendHeight);

    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    allItems.forEach((item, index) => {
      const row = Math.floor(index / itemsPerRow);
      const col = index % itemsPerRow;
      
      const itemX = legendX + padding + col * (boxWidth + spacing);
      const itemY = legendY + padding + row * (boxHeight + spacing);

      ctx.fillStyle = item.color;
      ctx.globalAlpha = 0.7;
      ctx.fillRect(itemX, itemY, boxWidth, boxHeight);
      ctx.globalAlpha = 1;

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.strokeRect(itemX, itemY, boxWidth, boxHeight);

      ctx.fillStyle = '#000000';
      const symbol = item.symbol || item.id;
      if (symbol.length > 4) {
        ctx.font = `bold ${fontSize - 1}px Arial`;
        const lines = symbol.match(/.{1,4}/g) || [symbol];
        lines.forEach((line, i) => {
          ctx.fillText(line, itemX + boxWidth / 2, itemY + boxHeight / 2 + (i - (lines.length - 1) / 2) * 10);
        });
        ctx.font = `bold ${fontSize}px Arial`;
      } else {
        ctx.fillText(symbol, itemX + boxWidth / 2, itemY + boxHeight / 2);
      }
    });
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleCanvasMouseDown = (e) => {
    const coords = getCanvasCoordinates(e);
    
    const clickedMarkIndex = marks.findIndex(mark => {
      const distance = Math.sqrt(
        Math.pow(coords.x - mark.x, 2) + Math.pow(coords.y - mark.y, 2)
      );
      return distance < 20;
    });

    if (clickedMarkIndex !== -1) {
      setSelectedMarkIndex(clickedMarkIndex);
      setIsDraggingMark(true);
      setDragOffset({
        x: coords.x - marks[clickedMarkIndex].x,
        y: coords.y - marks[clickedMarkIndex].y
      });
    } else if (selectedSymbol) {
      setMarks([...marks, {
        symbolId: selectedSymbol,
        x: coords.x,
        y: coords.y,
        timestamp: new Date().toISOString()
      }]);
      setSelectedMarkIndex(null);
    } else {
      setSelectedMarkIndex(null);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (isDraggingMark && selectedMarkIndex !== null) {
      const coords = getCanvasCoordinates(e);
      const newMarks = [...marks];
      newMarks[selectedMarkIndex] = {
        ...newMarks[selectedMarkIndex],
        x: coords.x - dragOffset.x,
        y: coords.y - dragOffset.y
      };
      setMarks(newMarks);
    }
  };

  const handleCanvasMouseUp = () => {
    setIsDraggingMark(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const symbolId = e.dataTransfer.getData('symbolId');
    if (!symbolId) return;

    const coords = getCanvasCoordinates(e);
    const canvas = canvasRef.current;
    
    if (coords.x >= 0 && coords.x <= canvas.width && 
        coords.y >= 0 && coords.y <= canvas.height) {
      setMarks([...marks, {
        symbolId,
        x: coords.x,
        y: coords.y,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleDragStart = (e, symbolId) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('symbolId', symbolId);
  };

  const handleUndo = () => {
    if (marks.length > 0) {
      setMarks(marks.slice(0, -1));
      setSelectedMarkIndex(null);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedMarkIndex !== null) {
      const newMarks = marks.filter((_, index) => index !== selectedMarkIndex);
      setMarks(newMarks);
      setSelectedMarkIndex(null);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all marks?')) {
      setMarks([]);
      setSelectedMarkIndex(null);
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png', 1.0);
    
    onSave({
      imageData,
      marks,
      timestamp: new Date().toISOString(),
      imageType
    });
  };

  // ── Switch to DB image source ─────────────────────────────────────────────
  const handleUseDbImage = () => {
    if (dbImageAvailable) {
      setImageSourceMode(IMAGE_SOURCE_MODE.DB);
      // Reload marks from existingImage if switching back to db mode
      if (existingImage?.marks) {
        setMarks(existingImage.marks);
      }
    }
  };

  // ── Switch to static image source ─────────────────────────────────────────
  const handleUseStaticImage = () => {
    setImageSourceMode(IMAGE_SOURCE_MODE.STATIC);
    setMarks([]);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header with Back Button */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">
          {legend.title}
        </h3>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
      </div>

      {/* Image Source Selector - shown when onhireId is provided */}
      {onhireId && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-semibold text-blue-800 mb-2">Background Image Source</p>
          {dbImageLoading ? (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Checking for existing image in database…
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {/* DB Image Button */}
              <button
                type="button"
                onClick={handleUseDbImage}
                disabled={!dbImageAvailable}
                title={dbImageAvailable ? 'Use the image saved in the database for this onhire' : 'No image found in the database for this onhire'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  imageSourceMode === IMAGE_SOURCE_MODE.DB
                    ? 'bg-blue-600 text-white'
                    : dbImageAvailable
                    ? 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                }`}
              >
                <Database className="w-4 h-4" />
                {dbImageAvailable ? 'Use Database Image' : 'No DB Image Found'}
              </button>

              {/* Static Image Button */}
              <button
                type="button"
                onClick={handleUseStaticImage}
                title="Use the default static tank diagram"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  imageSourceMode === IMAGE_SOURCE_MODE.STATIC
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'
                }`}
              >
                <Image className="w-4 h-4" />
                Use Static Image
              </button>

              {/* Active source indicator */}
              <span className="self-center text-xs text-gray-500 ml-2">
                {imageSourceMode === IMAGE_SOURCE_MODE.DB
                  ? '📂 Currently using: Database Image'
                  : '🖼️ Currently using: Static Template'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Edit Mode Toggle - Only show if existingImage has marks */}
      {existingImage?.marks?.length > 0 && (
        <div className="mb-4 flex gap-2 flex-wrap p-3 bg-blue-50 border border-blue-200 rounded-lg items-center">
          <span className="text-sm font-semibold text-blue-800 mr-2">Mode:</span>
          <button
            type="button"
            onClick={() => {
              setEditMode('edit');
              setMarks(existingImage.marks);
              setSelectedMarkIndex(null);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
              editMode === 'edit'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
            }`}
          >
            ✓ Edit Existing Marks ({existingImage.marks.length})
          </button>
          <button
            type="button"
            onClick={() => {
              setEditMode('new');
              setMarks([]);
              setSelectedMarkIndex(null);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
              editMode === 'new'
                ? 'bg-green-600 text-white'
                : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'
            }`}
          >
            + Start Fresh (Clear All Marks)
          </button>
          {editMode === 'edit' && (
            <span className="text-xs text-blue-600 ml-2">
              ← Click any mark on canvas to select &amp; move it
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Canvas Section */}
        <div className="lg:col-span-2">
          {/* TOP CONTROLS - Undo, Clear, Save */}
          <div className="mb-4 flex gap-2 flex-wrap items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleUndo}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                disabled={marks.length === 0}
                title="Undo last mark (Ctrl+Z)"
              >
                <Undo className="w-4 h-4" /> Undo
              </button>
              <button
                onClick={handleClear}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
                title="Clear all marks"
              >
                <Trash2 className="w-4 h-4" /> Clear Canvas
              </button>
            </div>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              title="Save canvas with marks"
            >
              <Download className="w-4 h-4" /> Save Image
            </button>
          </div>

          {/* Info Message */}
          <div className="mb-4 flex items-center gap-4 flex-wrap">
            {selectedMarkIndex !== null && (
              <span className="text-sm text-blue-600 font-medium flex items-center gap-1">
                <Move className="w-4 h-4" /> Mark selected - Drag to move or press Delete
              </span>
            )}
          </div>

          {/* Canvas */}
          <div 
            ref={containerRef}
            className="border-4 border-gray-400 rounded-lg overflow-hidden bg-white shadow-lg"
          >
            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="w-full bg-white cursor-crosshair"
              style={{ touchAction: 'none' }}
            />
          </div>

          {/* Additional Controls */}
          <div className="mt-4 flex gap-3 items-center">
            <button
              onClick={handleDeleteSelected}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
              disabled={selectedMarkIndex === null}
              title="Delete selected mark"
            >
              <Trash2 className="w-4 h-4" /> Delete Selected
            </button>
            <div className="text-sm text-gray-600 font-medium ml-auto">
              Marks placed: <span className="text-blue-600 font-bold text-lg">{marks.length}</span>
            </div>
          </div>
        </div>

        {/* Legend Section - Sidebar */}
        <div className="lg:col-span-1">
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white shadow-lg sticky top-4">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3">
              <h4 className="font-bold text-sm">LEGEND & SYMBOLS</h4>
              <p className="text-xs opacity-90">Click or drag to canvas</p>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto">
              {legend.categories.map((category, catIndex) => (
                <div key={catIndex} className="border-b border-gray-200 last:border-b-0">
                  {/* Category Header */}
                  <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
                    <h5 className="font-bold text-xs text-gray-800 uppercase tracking-wide">
                      {category.name}
                    </h5>
                  </div>
                  
                  {/* Items Table */}
                  <div className="divide-y divide-gray-200">
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        onClick={() => setSelectedSymbol(item.id)}
                        className={`flex items-center gap-2 p-2 cursor-pointer transition-all hover:bg-blue-50 ${
                          selectedSymbol === item.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''
                        }`}
                      >
                        {/* Symbol Box */}
                        <div
                          className="w-10 h-10 flex-shrink-0 rounded border-2 border-gray-800 flex items-center justify-center font-bold text-xs text-black shadow-sm"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.symbol || item.id}
                        </div>
                        
                        {/* Label */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {item.label}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 p-3 border-t border-gray-300">
              <p className="text-xs text-gray-600 mb-2 font-semibold">How to use:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Click a symbol, then click canvas</li>
                <li>• Or drag symbol to canvas</li>
                <li>• Click mark to select/move</li>
                <li>• Press Delete to remove selected</li>
                <li>• Ctrl+Z to undo</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TankCanvasEditor;